import type { Milliperecent } from './units.js';

/**
 * DrawingML colours are a base plus an ordered list of transforms. The order
 * matters — `lumMod` then `lumOff` is not the same as the reverse — so the
 * parser preserves the sequence and resolution replays it.
 */

export type ColorTransformOp =
  | 'alpha'
  | 'alphaMod'
  | 'alphaOff'
  | 'hueMod'
  | 'hueOff'
  | 'lumMod'
  | 'lumOff'
  | 'satMod'
  | 'satOff'
  | 'shade'
  | 'tint'
  | 'gray'
  | 'comp'
  | 'inv';

export interface ColorTransform {
  readonly op: ColorTransformOp;
  /** Thousandths of a percent. Absent for parameterless ops (`gray`, `inv`, `comp`). */
  readonly value?: Milliperecent;
}

/** The eight theme colour slots plus the two hyperlink slots. */
export type SchemeColorName =
  | 'dk1'
  | 'lt1'
  | 'dk2'
  | 'lt2'
  | 'accent1'
  | 'accent2'
  | 'accent3'
  | 'accent4'
  | 'accent5'
  | 'accent6'
  | 'hlink'
  | 'folHlink'
  /** Slide-relative aliases: resolved through the colour map on the master/slide. */
  | 'tx1'
  | 'tx2'
  | 'bg1'
  | 'bg2'
  | 'phClr';

export type ColorSpec =
  | { readonly kind: 'srgb'; readonly hex: string; readonly transforms: readonly ColorTransform[] }
  | {
      readonly kind: 'scheme';
      readonly name: SchemeColorName;
      readonly transforms: readonly ColorTransform[];
    }
  | { readonly kind: 'system'; readonly name: string; readonly lastHex?: string; readonly transforms: readonly ColorTransform[] }
  | { readonly kind: 'preset'; readonly name: string; readonly transforms: readonly ColorTransform[] }
  | { readonly kind: 'scrgb'; readonly r: number; readonly g: number; readonly b: number; readonly transforms: readonly ColorTransform[] };

export interface Rgba {
  /** 0-255 */
  readonly r: number;
  /** 0-255 */
  readonly g: number;
  /** 0-255 */
  readonly b: number;
  /** 0-1 */
  readonly a: number;
}

export function srgb(hex: string, transforms: readonly ColorTransform[] = []): ColorSpec {
  return { kind: 'srgb', hex: normaliseHex(hex), transforms };
}

export function schemeColor(
  name: SchemeColorName,
  transforms: readonly ColorTransform[] = [],
): ColorSpec {
  return { kind: 'scheme', name, transforms };
}

export function normaliseHex(hex: string): string {
  return hex.replace(/^#/, '').toUpperCase().padStart(6, '0').slice(0, 6);
}

export function rgbaToCss({ r, g, b, a }: Rgba): string {
  if (a >= 1) {
    return `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('')}`;
  }
  return `rgba(${clampByte(r)}, ${clampByte(g)}, ${clampByte(b)}, ${round(a, 4)})`;
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function round(n: number, places: number): number {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}
